import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { sendResetPasswordLink } from "../../redux/user/userSlice";
import toast from "react-hot-toast";
import { CheckCircleOutline } from "@mui/icons-material";
import { LuShield, LuUser, LuArrowLeft } from "react-icons/lu";
import headerLogo from "../../assets/images/Headerlogo_black.svg";

const OFF_WHITE = "#F1EBEB";
const DARK_NAVY = "#051525";
const DARK_NAVY_LIGHT = "#4a6278";
const BLUE = "#1E3A5F";
const BTN_LOGIN = "#1f3b64";
const BTN_HOVER = "#162C47";
const BTN_DISABLED = "#cfcaca";

const INPUT_FOCUS_GLOW =
  "0 0 0 3px rgba(30, 58, 95, 0.12), 0 4px 14px rgba(30, 58, 95, 0.18)";
const BTN_GLOW = "0 4px 18px rgba(31, 59, 100, 0.35)";

const INPUT_BORDER_DEFAULT = "#d0dff0";

const inputBaseStyle: React.CSSProperties = {
  borderWidth: "2px",
  borderStyle: "solid",
  borderColor: INPUT_BORDER_DEFAULT,
  boxShadow: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
};

const applyInputFocus = (el: HTMLInputElement) => {
  el.style.borderColor = BLUE;
  el.style.boxShadow = INPUT_FOCUS_GLOW;
};

const applyInputBlur = (el: HTMLInputElement) => {
  el.style.borderColor = INPUT_BORDER_DEFAULT;
  el.style.boxShadow = "none";
};

const helpSteps = [
  {
    title: "Quick password recovery",
    description: "Reset link sent to your registered email.",
  },
  {
    title: "Secure verification process",
    description: "Your account stays protected.",
  },
  {
    title: "24/7 support available",
    description: "We're here whenever you need us.",
  },
];

const ForgotPassword = () => {
  const dispatch = useDispatch<any>();
  const [userId, setUserId] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleForgotPassword = async () => {
    if (!userId.trim()) {
      toast.error("Please enter your User ID.");
      return;
    }
    try {
      const res = await dispatch(sendResetPasswordLink({ userId }));
      if (res?.error?.message) {
        toast.error("Something went wrong");
        return;
      }
      setIsEmailSent(true);
      toast.success("Reset link sent to your registered email.");
    } catch {
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: OFF_WHITE }}>
      {/* Header */}
      <header className="bg-[#000000] px-6 md:px-10 py-4 flex items-center justify-between shrink-0">
        <Link to="/">
          <img
            src={headerLogo}
            alt="AUM Sampann"
            className="h-10 md:h-12 w-auto object-contain"
            style={{
              filter:
                "brightness(0) saturate(100%) invert(94%) sepia(6%) saturate(500%) hue-rotate(314deg) brightness(103%) contrast(92%)",
            }}
          />
        </Link>
        <div className="flex items-center gap-2" style={{ color: OFF_WHITE }}>
          <LuShield className="w-5 h-5" />
          <span className="text-sm md:text-base font-medium">Admin Portal</span>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
        {/* Left panel */}
        <div
          className="hidden lg:flex lg:w-1/2 text-white relative overflow-hidden items-center justify-center"
          style={{
            background:
              "radial-gradient(ellipse 80% 70% at 50% 45%, #2a2a2a 0%, #1c1c1c 45%, #141414 100%)",
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `
                radial-gradient(circle at 50% 42%, transparent 18%, rgba(255,255,255,0.03) 18.5%, transparent 19%),
                radial-gradient(circle at 50% 42%, transparent 32%, rgba(255,255,255,0.025) 32.5%, transparent 33%),
                radial-gradient(circle at 50% 42%, transparent 48%, rgba(255,255,255,0.02) 48.5%, transparent 49%),
                radial-gradient(circle at 50% 42%, transparent 65%, rgba(255,255,255,0.015) 65.5%, transparent 66%)
              `,
            }}
          />

          <div className="relative z-10 flex flex-col px-12 xl:px-20 max-w-lg">
            <h1 className="text-[2rem] xl:text-[2.25rem] font-bold leading-tight text-white mb-5">
              We&apos;re Here to Help
            </h1>
            <p className="text-[15px] text-white/45 leading-relaxed mb-10 max-w-md">
              Enter your User ID and we&apos;ll email you a secure link to reset
              your password. It only takes a moment.
            </p>

            <div className="flex flex-col gap-6">
              {helpSteps.map(({ title, description }, index) => (
                <div key={title} className="flex items-start gap-4">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold text-white"
                    style={{ backgroundColor: BLUE }}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-[15px]">{title}</p>
                    <p className="text-white/45 text-sm mt-0.5">{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div
          className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-10"
          style={{ backgroundColor: OFF_WHITE }}
        >
          {isEmailSent ? (
            <div className="w-full max-w-[400px] bg-white rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.06)] px-8 py-10 flex flex-col items-center text-center">
              <CheckCircleOutline sx={{ fontSize: 72, color: "#08991F", mb: 2 }} />
              <h2 className="text-xl font-bold mb-3" style={{ color: DARK_NAVY }}>
                Check your email
              </h2>
              <p className="text-sm text-[#696969] leading-relaxed mb-6">
                A password reset link has been sent to your registered email.
                Please check your inbox and follow the instructions.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                style={{ color: BLUE }}
              >
                <LuArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          ) : (
            <div className="w-full max-w-[400px] bg-white rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.06)] px-8 py-9">
              <div className="text-left mb-7">
                <h2 className="text-2xl font-bold" style={{ color: DARK_NAVY }}>
                  Forgot Password?
                </h2>
                <p className="text-sm mt-1" style={{ color: DARK_NAVY_LIGHT }}>
                  Enter your User ID to receive a password reset link
                </p>
              </div>

              <div className="flex flex-col gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#000000] mb-1.5">
                    User ID
                  </label>
                  <div className="relative">
                    <LuUser
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
                      style={{ color: BLUE }}
                    />
                    <input
                      type="text"
                      name="userId"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      placeholder="Enter your registered user ID"
                      className="w-full pl-10 pr-4 py-3 bg-white rounded-lg text-sm text-[#000000] placeholder:text-[#9d9d9d] focus:outline-none"
                      style={inputBaseStyle}
                      onFocus={(e) => applyInputFocus(e.target)}
                      onBlur={(e) => applyInputBlur(e.target)}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  disabled={!userId.trim()}
                  onClick={handleForgotPassword}
                  className="w-full py-3 font-semibold rounded-lg transition-colors disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: !userId.trim() ? BTN_DISABLED : BTN_LOGIN,
                    color: !userId.trim() ? "#888888" : "#FFFFFF",
                    boxShadow: !userId.trim() ? "none" : BTN_GLOW,
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = BTN_HOVER;
                      e.currentTarget.style.boxShadow =
                        "0 6px 22px rgba(31, 59, 100, 0.45)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = BTN_LOGIN;
                      e.currentTarget.style.boxShadow = BTN_GLOW;
                    }
                  }}
                >
                  Send Reset Link
                </button>

                <p className="text-center text-xs text-[#9d9d9d]">
                  We&apos;ll send a reset link to your registered email
                </p>

                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 text-sm font-medium hover:underline"
                  style={{ color: BLUE }}
                >
                  <LuArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
